/**
 * Support Tab Component
 *
 * Shows support request history for an account.
 */
import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@respectly/ui';
import { HeadphonesIcon, ExternalLink, Clock, User, ChevronRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useSupportStore } from '../../../stores/useSupportStore';
import { SupportStatusBadge, SupportPriorityBadge } from '../../support';
import { formatDistanceToNow } from 'date-fns';

interface SupportTabProps {
  accountId: string;
}

export function SupportTab({ accountId }: SupportTabProps) {
  const { requests, isLoadingList, loadRequests, setFilter, clearFilters } = useSupportStore();

  useEffect(() => {
    // Load requests filtered by this account
    clearFilters();
    setFilter('tenantId', accountId);
    loadRequests();

    return () => {
      clearFilters();
    };
  }, [accountId, clearFilters, setFilter, loadRequests]);

  // Loading state
  if (isLoadingList) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HeadphonesIcon className="h-4 w-4" />
            Support History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg border p-3 animate-pulse">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
                  <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded-full w-16" />
                </div>
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2 mb-2" />
                <div className="flex items-center gap-2">
                  <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded-full w-14" />
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-20" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <div className="rounded-2xl bg-slate-100 p-4 dark:bg-slate-800">
            <HeadphonesIcon className="h-10 w-10 text-slate-400" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
            No Support Requests
          </h3>
          <p className="mt-1 text-center text-sm text-slate-500 max-w-sm">
            This account has no support request history.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <HeadphonesIcon className="h-4 w-4" />
          Support History
          <span className="text-xs font-normal text-slate-500">({requests.length})</span>
        </CardTitle>
        <Button variant="outline" size="sm" asChild>
          <Link to="/support" search={{ tenantId: accountId }}>
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            View in Inbox
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {requests.slice(0, 10).map((request) => (
            <Link
              key={request.id}
              to="/support"
              className="block rounded-lg border border-slate-200 dark:border-slate-700 p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="font-medium text-sm text-slate-900 dark:text-white line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                  {request.subject}
                </span>
                <SupportPriorityBadge priority={request.priority} size="sm" showDot={false} />
              </div>

              <p className="text-xs text-slate-500 line-clamp-1 mb-2">
                {request.description}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SupportStatusBadge status={request.status} size="sm" />
                </div>

                <div className="flex items-center gap-3 text-xs text-slate-400">
                  {request.assignedTo && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {request.assignedTo.firstName}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
                  </span>
                  <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </Link>
          ))}

          {requests.length > 10 && (
            <div className="text-center pt-2">
              <Button variant="link" size="sm" asChild>
                <Link to="/support" search={{ tenantId: accountId }}>
                  View all {requests.length} requests
                </Link>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
